import React, { useContext } from 'react';
import {
  AmplitudeEvents,
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import VideoComponent from '@components/common/VideoComponent/VideoComponent';
import { useEffect, useRef } from 'react';
import {
  CTATemplateUrl,
  CTAVideoWallTemplateUrl,
  CTAInvideoAiPromptPage,
} from '../../constants/makepage';
import {
  ClickCtaUxSource,
  ClickCtaType,
  AnalyticsProductType,
} from '../../types/analytics.types';
import { StateContext } from '@store/store';
import { BaseAppType } from '@constants/common';
import VideoComponentWithVolumeControls from '@components/common/VideoComponent/VideoComponentWithVolumeControls';

interface VideoTemplateProps {
  id: number;
  thumbnailURL: string;
  previewURL: string;
  play: boolean;
  width: number;
  height: number;
  isVideoWall?: boolean;
  isMiniVideoWall?: boolean;
  creatorName?: string;
  avatarURL?: string;
}

const AiActorTemplate = (props: VideoTemplateProps) => {
  const { baseAppType } = useContext(StateContext);
  const {
    thumbnailURL,
    previewURL,
    play,
    width,
    height,
    id,
    isVideoWall,
    isMiniVideoWall,
    creatorName, // = 'amyperez',
    avatarURL,
  } = props;

  const videoElement = useRef<HTMLVideoElement>(null);
  const videoElementPromise = useRef<Promise<void> | null>(null);
  const isMuted = true;
  const actorUrl = avatarURL || thumbnailURL;

  useEffect(() => {
    if (play) {
      playVideo();
    } else {
      pauseVideo();
    }
  }, [play]);

  const playVideo = () => {
    const video = videoElement?.current;
    if (video) {
      videoElementPromise.current = video.play();
    }
  };

  const pauseVideo = () => {
    const video = videoElement?.current;
    if (video && videoElementPromise.current) {
      videoElementPromise.current.then(() => {
        video.pause();
      });
      video.currentTime = 0;
    }
  };

  const openTemplate = () => {
    return false;
  };

  return (
    (thumbnailURL && (
      <div
        className={ivclass(
          'iv-w-full iv-h-full',
          'iv-cursor-pointer',
          'iv-relative',
        )}
      >
        <VideoComponentWithVolumeControls
          playsInline
          preload="none"
          loop
          {...(isMuted && { muted: true })}
          data-poster={thumbnailURL}
          className={ivclass(
            'iv-object-cover',
            'iv-h-full iv-w-full',
            'lazyload',
          )}
          ref={videoElement}
          onClick={openTemplate}
          isVideoWall={true}
        >
          <source data-src={previewURL} type="video/mp4" />
        </VideoComponentWithVolumeControls>
        <div className="iv-template-poster-image iv-absolute iv-top-0 iv-bottom-0 iv-h-full iv-w-full">
          <ImageComponent
            src={thumbnailURL}
            style={{
              width: '100%',
              height: 'auto',
            }}
            sizes="100vw"
            alt="logo"
            width={width}
            height={height}
            loading="lazy"
            elementype="next"
            onClick={openTemplate}
          />
        </div>

        {/* User profile strip - only shown if creatorName is provided */}
        {creatorName && (
          <div
            className={ivclass(
              'iv-absolute iv-bottom-0 iv-left-0 iv-right-0',
              'iv-px-10 iv-py-5',
              'iv-flex iv-items-center',
              'iv-z-10',
            )}
            style={{ backgroundColor: 'rgba(26, 26, 26, 0.7)' }}
          >
            {/* Avatar */}
            {actorUrl && (
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid #FFFFFF',
                  marginRight: '8px',
                  flexShrink: 0,
                }}
              >
                <ImageComponent
                  src={actorUrl}
                  alt={creatorName || 'user'}
                  width={30}
                  height={30}
                  elementype="next"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 40%',
                  }}
                />
              </div>
            )}
            {/* creatorName */}
            <span
              className={ivclass(
                'iv-text-body-s',
                'iv-font-medium',
                'iv-truncate',
              )}
              style={{ color: '#FFFFFF' }}
            >
              {creatorName}
            </span>
          </div>
        )}
      </div>
    )) || <React.Fragment></React.Fragment>
  );
};

export default AiActorTemplate;
